window.ConvexHullGrahamScan = require('graham_scan')
/**
 * @name Clarker marker clusterer for Google Maps v3
 * @version version .5
 * @author Phillip Holmstrand
 * @fileoverview
 * The library creates and manages per-zoom-level clusters for large amounts of
 * markers. It is a fork of the MarkerClusterer library
 * http://google-maps-utility-library-v3.googlecode.com/svn/trunk/markerclusterer/
 */
/**
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * @param {google.maps.Map} map The Google map to attach to.
 * @param {Array.<google.maps.Marker>=} opt_markers Optional markers to add to
 *   the cluster.
 * @param {Object=} opt_options support the following options:
 *     'gridSize': (number) The grid size of a cluster in pixels.
 *     'maxZoom': (number) The maximum zoom level that a marker can be part of a
 *                cluster.
 *     'zoomOnClick': (boolean) Whether the default behaviour of clicking on a
 *                    cluster is to zoom into it.
 *     'averageCenter': (boolean) Wether the center of each cluster should be
 *                      the average of all markers in the cluster.
 *     'minimumClusterSize': (number) The minimum number of markers to be in a
 *                           cluster before the markers are hidden and a count
 *                           is shown.
 *     'pieHolePercent': (number) A number between 0 and 0.9 used to inform the size(percent)
 *                       of the pie/donut hole relative to the piecharts total size. 
 *     'styles': (object) An object that has style properties:
 *       'url': (string) The image url.
 *       'height': (number) The image height.
 *       'width': (number) The image width.
 *       'textColor': (string) The text color.
 *       'textSize': (number) The text size.
 *       'backgroundPosition': (string) The position of the backgound x, y.
 * @constructor
 * @extends google.maps.OverlayView
 */
function Clarker(map, opt_markers, opt_options) {
    // MarkerClusterer implements google.maps.OverlayView interface. We use the
    // extend function to extend MarkerClusterer with google.maps.OverlayView
    // because it might not always be available when the code is defined so we
    // look for it at the last possible moment. If it doesn't exist now then
    // there is no point going ahead :)
    this.extend(Clarker, google.maps.OverlayView);
    this.map_ = map;

    /**
     * @type {Array.<google.maps.Marker>}
     * @private
     */
    this.markers_ = [];

    /**
     *  @type {Array.<Cluster>}
     */
    this.clusters_ = [];

    this.sizes = [34, 40, 52, 64, 72];

    /**
     * @type {boolean}
     * @private
    */
    this.ready_ = false;
    
    var options = opt_options || {};

    /**
    * @private
    */
    this.styles_ = options.styles || [];

    /**
    * @type {number}
    * @private
    */
    this.pieHolePercent_ = this.calculatePieHolePercent(options.pieHolePercent);

    /**
     * @type {number}
     * @private
     */
    this.gridSize_ = options.gridSize || 64;

    /**
     * @private
     */
    this.minClusterSize_ = options.minimumClusterSize || 4;

    /**
     * @type {?number}
     * @private
     */
    this.maxZoom_ = options.maxZoom || null;

    /**
     * @type {string}
     * @private
     */
    this.imagePath_ = options.imagePath ||
        this.MARKER_CLUSTER_IMAGE_PATH_;

    /**
     * @type {string}
     * @private
     */
    this.imageExtension_ = options.imageExtension ||
        this.MARKER_CLUSTER_IMAGE_EXTENSION_;

    /**
     * @type {boolean}
     * @private
     */
    this.zoomOnClick_ = true;

    if (options.zoomOnClick !== undefined) {
        this.zoomOnClick_ = options.zoomOnClick;
    }

    /**
     * @type {boolean}
     * @private
     */
    this.averageCenter_ = true;

    if (options.averageCenter !== undefined) {
        this.averageCenter_ = options.averageCenter;
    }

    this.setupStyles_();

    this.setMap(map);

    /**
     * @type {number}
     * @private
     */
    this.prevZoom_ = this.map_.getZoom();

    // Add the map event listeners
    var that = this;
    google.maps.event.addListener(this.map_, 'zoom_changed', function () {
        var maxZoom;
        try { // PKH added try block, this was throwing errors on maxZoom being missing sometimes
            maxZoom = that.map_.mapTypes[that.map_.getMapTypeId()].maxZoom;
        } catch (err) {
            return;
        }
        var zoom = that.map_.getZoom();
        if (zoom < 0 || zoom > maxZoom) {
            return;
        }

        if (that.prevZoom_ != zoom) {
            that.prevZoom_ = that.map_.getZoom();
            that.resetViewport();
        }
    });

    google.maps.event.addListener(this.map_, 'idle', function () {
        that.repaint();
        if (BatchGeoStore.getState().Cluster.enabled) window.batchGeoGoogleMapsMarker.MarkerDrawer.checkLabels();
    });

    // Finally, add the markers
    if (opt_markers && opt_markers.length) {
        this.addMarkers(opt_markers, false);
    }
}

/**
 * An array of cluster image objects that couldn't be loaded because the pie
 * chart library was not yet loaded. Once the library is loaded this should be
 * cleared out by calling Clarker._runClusterQueue().
 *
 * @type {Array}
 * @private
 */
Clarker._clusterQueue = [];

/**
 * A list of loading statuses as enums for the Google Visualization library.
 *
 * @type {Object}
 * @private
 */
Clarker._clusterLoadStatusEnums = {
	NOT_REQUESTED: 'NOT_REQUESTED',
	LOADING: 'LOADING',
	LOADED: 'LOADED'
};

/**
 * A string value of the current status of the visualization library.
 *
 * @type {string}
 * @private
 */
Clarker._chartLibraryLoadStatus = Clarker._clusterLoadStatusEnums.NOT_REQUESTED;

/**
 * The marker cluster image path.
 *
 * @type {string}
 * @private
 */
Clarker.prototype.MARKER_CLUSTER_IMAGE_EXTENSION_ = 'png?v=2';

/**
 * Extends a objects prototype by anothers.
 *
 * @param {Object} obj1 The object to be extended.
 * @param {Object} obj2 The object to extend with.
 * @return {Object} The new extended object.
 * @ignore
 */
Clarker.prototype.extend = function (obj1, obj2) {
    return (function (object) {
        for (var property in object.prototype) {
            this.prototype[property] = object.prototype[property];
        }
        return this;
    }).apply(obj1, [obj2]);
};


/**
 * Implementaion of the interface method.
 * @ignore
 */
Clarker.prototype.onAdd = function () {
    this.setReady_(true);
};

/**
 * Implementaion of the interface method.
 * @ignore
 */
Clarker.prototype.draw = function () {};

/**
 * Sets up the styles object.
 *
 * @private
 */
Clarker.prototype.setupStyles_ = function () {
    var per = window.batchGeoGoogleMapsMarker.options.markersData;
    if (per.mapRS) {
        for (var i = 0, size; size = this.sizes[i]; i++) { // jshint ignore:line
            this.styles_.push({
                url: Clarker.prototype.MARKER_CLUSTER_IMAGE_PATH_ + (i + 1) + window.per.mapRS[0].clr + BatchGeo.getRetinaSuffix() + '.' + this.imageExtension_,
                height: size,
                width: size
            });
        }
    }
};

/**
 *  Fit the map to the bounds of the markers in the clusterer.
 */
Clarker.prototype.fitMapToMarkers = function () {
    var markers = this.getMarkers();
    var bounds = new google.maps.LatLngBounds();
    for (var i = 0, marker; marker = markers[i]; i++) { // jshint ignore:line
        bounds.extend(marker.getPosition());
    }

    this.map_.fitBounds(bounds);
};


/**
 *  Sets the styles.
 *
 *  @param {Object} styles The style to set.
 */
Clarker.prototype.setStyles = function (styles) {
    this.styles_ = styles;
};


/**
 *  Gets the styles.
 *
 *  @return {Object} The styles object.
 */
Clarker.prototype.getStyles = function () {
    return this.styles_;
};

/**
 * Gets the pieHole percent state
 * 
 * @return {number} A number between 0 and 1
 */
Clarker.prototype.getPieHolePercent = function () {
    return this.pieHolePercent_;
}

/**
 * Sets the piehold percent state
 * 
 * @param {*} php 
 * @returns {number} A number between 0 and 0.9
 */
Clarker.prototype.calculatePieHolePercent = function (php) {
    if (php === undefined) {
        // default
        return 0.8;
    } else if (php > 0.9) {
        // 90% max
        return 0.9;
    } else if (php < 0) {
        // 0% min
        return 0;
    } else {
        return php;
    }
}

/**
 * Gets a hash of clusters by their id
 * 
 * @returns {object} Cluster object
 */
Clarker.prototype.getClustersByIdHash = function () {
    const clustersById = {};
    this.clusters_.forEach((cluster) => {
        clustersById[cluster.id] = cluster
    });
    return clustersById;
}

/**
 * Whether zoom on click is set.
 *
 * @return {boolean} True if zoomOnClick_ is set.
 */
Clarker.prototype.isZoomOnClick = function () {
    return this.zoomOnClick_;
};

/**
 * Whether average center is set.
 *
 * @return {boolean} True if averageCenter_ is set.
 */
Clarker.prototype.isAverageCenter = function () {
    return this.averageCenter_;
};


/**
 *  Returns the array of markers in the clusterer.
 *
 *  @return {Array.<google.maps.Marker>} The markers.
 */
Clarker.prototype.getMarkers = function () {
    return this.markers_;
};


/**
 *  Returns the number of markers in the clusterer
 *
 *  @return {number} The number of markers.
 */
Clarker.prototype.getTotalMarkers = function () {
    return this.markers_.length;
};


/**
 *  Sets the max zoom for the clusterer.
 *
 *  @param {number} maxZoom The max zoom level.
 */
Clarker.prototype.setMaxZoom = function (maxZoom) {
    this.maxZoom_ = maxZoom;
};


/**
 *  Gets the max zoom for the clusterer.
 *
 *  @return {number} The max zoom level.
 */
Clarker.prototype.getMaxZoom = function () {
    return this.maxZoom_ || this.map_.mapTypes[this.map_.getMapTypeId()].maxZoom;
};


/**
 *  The function for calculating the cluster icon image.
 *
 *  @param {Array.<google.maps.Marker>} markers The markers in the clusterer.
 *  @param {number} numStyles The number of styles available.
 *  @return {Object} A object properties: 'text' (string) and 'index' (number).
 *  @private
 */
Clarker.prototype.calculator_ = function (markers, numStyles) {
    var index = 0;
    var count = markers.length;
    var dv = count;
    while (dv !== 0) {
        dv = parseInt(dv / 10, 10);
        index++;
    }

    index = Math.min(index, numStyles);
    return {
        text: count,
        index: index
    };
};


/**
 * Set the calculator function.
 *
 * @param {function(Array, number)} calculator The function to set as the
 *     calculator. The function should return a object properties:
 *     'text' (string) and 'index' (number).
 *
 */
Clarker.prototype.setCalculator = function (calculator) {
    this.calculator_ = calculator;
};


/**
 * Get the calculator function.
 *
 * @return {function(Array, number)} the calculator function.
 */
Clarker.prototype.getCalculator = function () {
    return this.calculator_;
};


/**
 * Add an array of markers to the clusterer.
 *
 * @param {Array.<google.maps.Marker>} markers The markers to add.
 * @param {boolean=} opt_nodraw Whether to redraw the clusters.
 */
Clarker.prototype.addMarkers = function (markers, opt_nodraw) {
    for (var i = 0, marker; marker = markers[i]; i++) { // jshint ignore:line
        this.pushMarkerTo_(marker);
    }
    if (!opt_nodraw) {
        this.redraw();
    }
};


/**
 * Pushes a marker to the clusterer.
 *
 * @param {google.maps.Marker} marker The marker to add.
 * @private
 */
Clarker.prototype.pushMarkerTo_ = function (marker) {
    marker.isAdded = false;
    if (marker.draggable) {
        // If the marker is draggable add a listener so we update the clusters on
        // the drag end.
        var that = this;
        google.maps.event.addListener(marker, 'dragend', function () {
            marker.isAdded = false;
            that.repaint();
        });
    }
    this.markers_.push(marker);
};


/**
 * Adds a marker to the clusterer and redraws if needed.
 *
 * @param {google.maps.Marker} marker The marker to add.
 * @param {boolean=} opt_nodraw Whether to redraw the clusters.
 */
Clarker.prototype.addMarker = function (marker, opt_nodraw) {
    this.pushMarkerTo_(marker);
    if (!opt_nodraw) {
        this.redraw();
    }
};


/**
 * Removes a marker and returns true if removed, false if not
 *
 * @param {google.maps.Marker} marker The marker to remove
 * @return {boolean} Whether the marker was removed or not
 * @private
 */
Clarker.prototype.removeMarker_ = function (marker) {
    var index = -1;
    if (this.markers_.indexOf) {
        index = this.markers_.indexOf(marker);
    } else {
        for (var i = 0, m; m = this.markers_[i]; i++) { // jshint ignore:line
            if (m == marker) {
                index = i;
                break;
            }
        }
    }

    if (index == -1) {
        // Marker is not in our list of markers.
        return false;
    }

    this.markers_.splice(index, 1);

    return true;
};


/**
 * Remove a marker from the cluster.
 *
 * @param {google.maps.Marker} marker The marker to remove.
 * @param {boolean=} opt_nodraw Optional boolean to force no redraw.
 * @return {boolean} True if the marker was removed.
 */
Clarker.prototype.removeMarker = function (marker, opt_nodraw) {
    var removed = this.removeMarker_(marker);

    if (!opt_nodraw && removed) {
        this.resetViewport();
        this.redraw();
        return true;
    } else {
        return false;
    }
};


/**
 * Removes an array of markers from the cluster.
 *
 * @param {Array.<google.maps.Marker>} markers The markers to remove.
 * @param {boolean=} opt_nodraw Optional boolean to force no redraw.
 */
Clarker.prototype.removeMarkers = function (markers, opt_nodraw) {
    var removed = false;

    for (var i = 0, marker; marker = markers[i]; i++) { // jshint ignore:line
        var r = this.removeMarker_(marker);
        removed = removed || r;
    }

    if (!opt_nodraw && removed) {
        this.resetViewport();
        this.redraw();
        return true;
    }
};


/**
 * Sets the clusterer's ready state.
 *
 * @param {boolean} ready The state.
 * @private
 */
Clarker.prototype.setReady_ = function (ready) {
    if (!this.ready_) {
        this.ready_ = ready;
        this.createClusters_();
    }
};


/**
 * Returns the number of clusters in the clusterer.
 *
 * @return {number} The number of clusters.
 */
Clarker.prototype.getTotalClusters = function () {
    return this.clusters_.length;
};


/**
 * Returns the google map that the clusterer is associated with.
 *
 * @return {google.maps.Map} The map.
 */
Clarker.prototype.getMap = function () {
    return this.map_;
};


/**
 * Sets the google map that the clusterer is associated with.
 *
 * @param {google.maps.Map} map The map.
 */
Clarker.prototype.setMap = function (map) {
    this.map_ = map;
};


/**
 * Returns the size of the grid.
 *
 * @return {number} The grid size.
 */
Clarker.prototype.getGridSize = function () {
    return this.gridSize_;
};


/**
 * Sets the size of the grid.
 *
 * @param {number} size The grid size.
 */
Clarker.prototype.setGridSize = function (size) {
    this.gridSize_ = size;
};


/**
 * Returns the min cluster size.
 *
 * @return {number} The grid size.
 */
Clarker.prototype.getMinClusterSize = function () {
    return this.minClusterSize_;
};

/**
 * Sets the min cluster size.
 *
 * @param {number} size The grid size.
 */
Clarker.prototype.setMinClusterSize = function (size) {
    this.minClusterSize_ = size;
};


/**
 * Extends a bounds object by the grid size.
 *
 * @param {google.maps.LatLngBounds} bounds The bounds to extend.
 * @return {google.maps.LatLngBounds} The extended bounds.
 */
Clarker.prototype.getExtendedBounds = function (bounds) {
    var projection = this.getProjection();

    // Turn the bounds into latlng.
    var tr = new google.maps.LatLng(bounds.getNorthEast().lat(),
        bounds.getNorthEast().lng());
    var bl = new google.maps.LatLng(bounds.getSouthWest().lat(),
        bounds.getSouthWest().lng());

    // Convert the points to pixels and the extend out by the grid size.
    var trPix = projection.fromLatLngToDivPixel(tr);
    trPix.x += this.gridSize_;
    trPix.y -= this.gridSize_;

    var blPix = projection.fromLatLngToDivPixel(bl);
    blPix.x -= this.gridSize_;
    blPix.y += this.gridSize_;

    // Convert the pixel points back to LatLng
    var ne = projection.fromDivPixelToLatLng(trPix);
    var sw = projection.fromDivPixelToLatLng(blPix);

    // Extend the bounds to contain the new bounds.
    bounds.extend(ne);
    bounds.extend(sw);

    return bounds;
};


/**
 * Determins if a marker is contained in a bounds.
 *
 * @param {google.maps.Marker} marker The marker to check.
 * @param {google.maps.LatLngBounds} bounds The bounds to check against.
 * @return {boolean} True if the marker is in the bounds.
 * @private
 */
Clarker.prototype.isMarkerInBounds_ = function (marker, bounds) {
    return bounds.contains(marker.getPosition());
};


/**
 * Clears all clusters and markers from the clusterer.
 */
Clarker.prototype.clearMarkers = function () {
    this.resetViewport(true);

    // Set the markers a empty array.
    this.markers_ = [];
};


/**
 * Clears all existing clusters and recreates them.
 * @param {boolean} opt_hide To also hide the marker.
 */
Clarker.prototype.resetViewport = function (opt_hide) {
    // Remove all the clusters
    for (var i = 0, cluster; cluster = this.clusters_[i]; i++) { // jshint ignore:line
        cluster.remove();
    }

    // Reset the markers to not be added and to be invisible.
    for (var i = 0, marker; marker = this.markers_[i]; i++) { // jshint ignore:line
        marker.isAdded = false;
        if (opt_hide) {
            marker.setMap(null);
        }
    }

    this.clusters_ = [];
};

/**
 *
 */
Clarker.prototype.repaint = function () {
    var oldClusters = this.clusters_.slice();
    this.clusters_.length = 0;
    this.resetViewport();
    this.redraw();

    // Remove the old clusters.
    // Do it in a timeout so the other clusters have been drawn first.
    window.setTimeout(function () {
        for (var i = 0, cluster; cluster = oldClusters[i]; i++) { // jshint ignore:line
            cluster.remove();
        }
    }, 0);
};


/**
 * Redraws the clusters.
 */
Clarker.prototype.redraw = function () {
    this.createClusters_();
};


/**
 * Calculates the distance between two latlng locations in km.
 * @see http://www.movable-type.co.uk/scripts/latlong.html
 *
 * @param {google.maps.LatLng} p1 The first lat lng point.
 * @param {google.maps.LatLng} p2 The second lat lng point.
 * @return {number} The distance between the two points in km.
 * @private
 */
Clarker.prototype.distanceBetweenPoints_ = function (p1, p2) {
    if (!p1 || !p2) {
        return 0;
    }

    var R = 6371; // Radius of the Earth in km
    var dLat = (p2.lat() - p1.lat()) * Math.PI / 180;
    var dLon = (p2.lng() - p1.lng()) * Math.PI / 180;
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(p1.lat() * Math.PI / 180) * Math.cos(p2.lat() * Math.PI / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    var d = R * c;
    return d;
};


/**
 * Add a marker to a cluster, or creates a new cluster.
 *
 * @param {google.maps.Marker} marker The marker to add.
 * @private
 */
Clarker.prototype.addToClosestCluster_ = function (marker, isMarkerSelected) {
    var distance = 40000; // Some large number
    var clusterToAddTo = null;
    for (var i = 0, cluster; cluster = this.clusters_[i]; i++) { // jshint ignore:line
        var center = cluster.getCenter();
        if (center) {
            var d = this.distanceBetweenPoints_(center, marker.getPosition());
            if (d < distance) {
                distance = d;
                clusterToAddTo = cluster;
            }
        }
    }

    if (clusterToAddTo && clusterToAddTo.isMarkerInClusterBounds(marker)) {
        clusterToAddTo.addMarker(marker, isMarkerSelected);
    } else {
        var addcluster = new Cluster(this);
        addcluster.addMarker(marker, isMarkerSelected);
        this.clusters_.push(addcluster);
    }
};

/**
 * Creates the clusters.
 *
 * @private
 */
Clarker.prototype.createClusters_ = function () {
    if (!this.map_.getBounds()) {
        return;
    }
    
    var mapBounds, bounds;
    const selectedMarkerIdHash = this.getSelectedMarkersObj();

    // Get our current map view bounds.
    // Create a new bounds object so we don't affect the map.
    /* temp workaround for google bug - pkh - */
    if (this.map_.getZoom() <= 3) {
        mapBounds = new google.maps.LatLngBounds(new google.maps.LatLng(85.02070771743472, -178.48388434375), new google.maps.LatLng(-85.08136444384544, 178.00048865625));
    } else {
        mapBounds = new google.maps.LatLngBounds(this.map_.getBounds().getSouthWest(), this.map_.getBounds().getNorthEast());
    }

    bounds = this.getExtendedBounds(mapBounds);

    for (var i = 0, marker; marker = this.markers_[i]; i++) { // jshint ignore:line
        if (!marker.isAdded && this.isMarkerInBounds_(marker, bounds)) {
            this.addToClosestCluster_(marker, selectedMarkerIdHash[marker.id]);
        }
    }
    for(var i = 0, cluster; cluster = this.clusters_[i]; i++){
        cluster.calculateClusterVisability();
    }
    
    Clarker._runClusterQueue();
    Clarker._clusterQueue = []; // Clear the queue after processing
    
    if (!this.ready_) {
        this.setReady_(true);
    }
    window.batchGeoGoogleMapsMarker.MarkerDrawer.checkLabels();
};

/**
 * Runs through all Clusters and determines if markers should be shown or hidden
*
 * @warning Only process markers show / hide after all clusters have been created - PHK
 */
Clarker.prototype.showMarkers = function () {
	BatchGeo.timer('showMarkers()');
    var map_bounds = this.map_.getBounds();
    for (var i = 0; i < this.clusters_.length; i++) {
        var clst = this.clusters_[i];
        if (!map_bounds.intersects(clst.bounds_)) {
            continue;
        }
        var len = clst.markers_.length;
        var c;
        if (len < clst.minClusterSize_) {
            // Min cluster size not reached so show the marker.
            for (c = 0; c < len; c++) {
                if (clst.markers_[c].getMap() != this.map_) {
                    clst.markers_[c].setMap(this.map_);
                }
            }
        } else {
            for (c = 0; c < len; c++) {
                clst.markers_[c].setMap(null);
            }
        }
    }
};

/**
 * Gets a hash of selected markers by their row index (`marker.r` value)
 * 
 * @returns {object}
 */
Clarker.prototype.getSelectedMarkersObj = function () {
    return BatchGeoStore.getState().RowSelect.selection.reduce((hash, curr) => {
        hash[curr] = true;
        return hash;
    }, {});
};

Clarker._runClusterQueue = function () {
	Clarker._clusterQueue.forEach(function (clusterInstance) {
		clusterInstance.updateBling();
	});
}
Clarker._pushToClusterQueue = function (clusterInstance) {
	Clarker._clusterQueue.push(clusterInstance);
}

Clarker.prototype.checkClusters = function() {
    const selectedMarkerIdHash = this.getSelectedMarkersObj();
    this.clusters_.forEach((cluster) => {
        const selectedMarkers = cluster.markers_.map((marker) => selectedMarkerIdHash[marker.r] ?? false);
        if (cluster.isIconSelected && selectedMarkers.indexOf(false) !== -1) {
            cluster.deselect();
        }

        if (!cluster.isIconSelected && selectedMarkers.indexOf(false) === -1) {
            cluster.select();
        }
    });
}

/**
 * Hash of seen/used Cluster IDs from current session.
 * 
 * Passed to BatchGeo.volatileUID().
 */
var volatileSeenClusterIconIds = {};

/**
 * A cluster that contains markers.
 *
 * @param {Clarker} markerClusterer The markerclusterer that this
 *     cluster is associated with.
 * @constructor
 * @ignore
 */
function Cluster(markerClusterer) {
    var per = window.batchGeoGoogleMapsMarker.options.markersData;
    this.markerClusterer_ = markerClusterer;
    this.map_ = markerClusterer.getMap();
    this.gridSize_ = markerClusterer.getGridSize();
    if (per.clustering_icon_col > -1 && per.clustering_icon_cb_status) {
      this.minClusterSize_ = 1;
    } else {
      this.minClusterSize_ = markerClusterer.getMinClusterSize();
    }
    this.averageCenter_ = markerClusterer.isAverageCenter();
    this.center_ = null;
    this.markers_ = [];
    this.bounds_ = null;
    this.clusterIcon_ = new ClusterIcon(
        this, 
        markerClusterer.getStyles(),
        markerClusterer.getPieHolePercent(),
        markerClusterer.getGridSize()
    );
    this.isIconSelected = true;
    this.convexHull = null;
    this.id = BatchGeo.volatileUID(volatileSeenClusterIconIds);
}

/**
 * Does nothing if new state is same as old state.
 * Sets `isIconSelect` to true.
 * Calls clusterIcon's `setActive` method if available
 */
Cluster.prototype.select = function() {
    if(!this.isIconSelected) {
        this.isIconSelected = true;
        this.clusterIcon_?.setActive();
    }
}

/**
 * Does nothing if new state is same as old state.
 * Sets `isIconSelect` to false.
 * Calls clusterIcon's `setInactive` method if available
 */
Cluster.prototype.deselect = function() {
    if (this.isIconSelected) {
        this.isIconSelected = false;
        this.clusterIcon_?.setInactive();
    }
}

/**
 * Determins if a marker is already added to the cluster.
 *
 * @param {google.maps.Marker} marker The marker to check.
 * @return {boolean} True if the marker is already added.
 */
Cluster.prototype.isMarkerAlreadyAdded = function (marker) {
    if (this.markers_.indexOf) {
        return this.markers_.indexOf(marker) != -1;
    } else {
        for (var i = 0, m; m = this.markers_[i]; i++) { // jshint ignore:line
            if (m == marker) {
                return true;
            }
        }
    }
    return false;
};

Cluster.prototype.countVisibleMarkers = function () {
    const knownLocations = [];
    // sometimes this gets called early, return 0 it'll be recalculated later
    if (!this.clusterIcon_.getProjection()) {
        return 0;
    }

    // in general the cluster updates way too frequently and could be cut down
    const c = this.gridSize_ / Math.pow(3, this.getMap().zoom);
    for (const marker of this.markers_) {
        const location = this.clusterIcon_.getProjection().fromLatLngToDivPixel(marker.getPosition());
        if (!knownLocations.some((kl) => {
            const a = kl.x - location.x;
            const b = kl.y - location.y;
            return a * a + b * b < c * c;
        })) {
            knownLocations.push(location);
        }
    }

    return knownLocations.length;
}

/**
 * Add a marker the cluster.
 *
 * @param {google.maps.Marker} marker The marker to add.
 * @return {boolean} True if the marker was added.
 */
Cluster.prototype.addMarker = function (marker, isSelected) {
    if (this.isMarkerAlreadyAdded(marker)) {
        return false;
    }

    if (this.isIconSelected && !isSelected) {
        this.deselect();
    }

    if (!this.center_) {
        this.center_ = marker.getPosition();
        this.calculateBounds_();
    } else {
        if (this.averageCenter_) {
            var l = this.markers_.length + 1;
            var lat = (this.center_.lat() * (l - 1) + marker.getPosition().lat()) / l;
            var lng = (this.center_.lng() * (l - 1) + marker.getPosition().lng()) / l;
            this.center_ = new google.maps.LatLng(lat, lng);
            this.calculateBounds_();
        }
    }

    marker.isAdded = true;
    this.markers_.push(marker);
    return true;
};

/**
 * Returns the marker clusterer that the cluster is associated with.
 *
 * @return {Clarker} The associated marker clusterer.
 */
Cluster.prototype.getMarkerClusterer = function () {
    return this.markerClusterer_;
};

/**
 * Returns the bounds of the cluster.
 *
 * @return {google.maps.LatLngBounds} the cluster bounds.
 */
Cluster.prototype.getBounds = function () {
    var bounds = new google.maps.LatLngBounds(this.center_, this.center_);
    var markers = this.getMarkers();
    for (var i = 0, marker; marker = markers[i]; i++) { // jshint ignore:line
        bounds.extend(marker.getPosition());
    }
    return bounds;
};


/**
 * Removes the cluster
 */
Cluster.prototype.remove = function () {
    this.clusterIcon_.remove();
    this.markers_.length = 0;
    this.markers_ = [];

    if (this.convexHull) {
        this.convexHull.setMap(null);
        this.convexHull = null;
    }
};


/**
 * Returns the number of markers in the cluster.
 *
 * @return {number} The cluster's marker array length.
 */
Cluster.prototype.getSize = function () {
    return this.markers_.length;
};


/**
 * Returns the cluster's markers.
 *
 * @return {Array.<google.maps.Marker>} The cluster's markers.
 */
Cluster.prototype.getMarkers = function () {
    return this.markers_;
};


/**
 * Returns the center of the cluster.
 *
 * @return {google.maps.LatLng} The cluster center.
 */
Cluster.prototype.getCenter = function () {
    return this.center_;
};


/**
 * Calculated the extended bounds of the cluster with the grid.
 *
 * @private
 */
Cluster.prototype.calculateBounds_ = function () {
    var bounds = new google.maps.LatLngBounds(this.center_, this.center_);
    this.bounds_ = this.markerClusterer_.getExtendedBounds(bounds);
};


/**
 * Determines if a marker lies in the clusters bounds.
 *
 * @param {google.maps.Marker} marker The marker to check.
 * @return {boolean} True if the marker lies in the bounds.
 */
Cluster.prototype.isMarkerInClusterBounds = function (marker) {
    return this.bounds_.contains(marker.getPosition());
};


/**
 * Returns the map that the cluster is associated with.
 *
 * @return {google.maps.Map} The map.
 */
Cluster.prototype.getMap = function () {
    return this.map_;
};


/**
 * Updates the cluster icon
 */
Cluster.prototype.updateIcon = function (showCluster) {
    var zoom = this.map_.getZoom();
    var mz = this.markerClusterer_.getMaxZoom();

    if (zoom > mz) {
        // The zoom is greater than our max zoom so show all the markers in cluster.
        for (var i = 0, marker; marker = this.markers_[i]; i++) { // jshint ignore:line
            marker.setMap(this.map_);
        }
        return;
    }

    if (!showCluster) {
        // Min cluster size not yet reached.
        this.clusterIcon_.hide();
        return;
    }

    var numStyles = this.markerClusterer_.getStyles().length;
    var sums = this.markerClusterer_.getCalculator()(this.markers_, numStyles);

    this.clusterIcon_.setCenter(this.center_);
    this.clusterIcon_.setSums(sums);
    this.clusterIcon_.show();
};

/**
 * Creates a new convex hull if none exists, or shows an existing one 
 */
Cluster.prototype.createOrShowHull = function() {
    if (this.convexHull) {
        this.convexHull.setMap(this.map_);
    } else {
        this.convexHull = new ConvexHull(this.markers_);
        this.convexHull.setMap(this.map_);
    }
}

/**
 * Hides the currently displayed convex hull 
 */
Cluster.prototype.hideHull = function() {
    if (this.convexHull) {
        this.convexHull.setMap(null);
    }
}
Cluster.prototype.calculateClusterVisability = function() {
    var showCluster = this.countVisibleMarkers() >= this.minClusterSize_
    for (var i = 0; i < this.markers_.length; i++) {
        this.markers_[i].setMap(showCluster ? null : this.map_);
    }

    this.updateIcon(showCluster);
}
/**
 * A polygon representing this cluster's convex hull (outline)
 * @param {Array} markers
 */
function ConvexHull(markers) {
    // Functions
    this.pointArrayToLatLng = function(points) {
        var output = [];
        for (var i = 0; i < points.length; i++) {
            output.push(new google.maps.LatLng(points[i].y, points[i].x));
        }
        return output;
    }

    this.getConvexHull = function() {
        return hull;
    }

    // Convenience wrapper
    this.setMap = function(map) {
        hull.setMap(map);
    }

    // Set up
    var convexHull = new ConvexHullGrahamScan();
    for (var i = 0; i < markers.length; i++) {
        convexHull.addPoint(markers[i].position.lng(), markers[i].position.lat());
    }
    var hullPoints = convexHull.getHull();

    // Create polygon
    var hull = new google.maps.Polygon({
        paths: this.pointArrayToLatLng(hullPoints),
        strokeColor: '#4285F4',
        strokeWeight: 1,
        fillColor: '#4285F4',
        fillOpacity: 0.4
    });
}

/**
 * A cluster icon
 *
 * @param {Cluster} cluster The cluster to be associated with.
 * @param {Object} styles An object that has style properties:
 *     'url': (string) The image url.
 *     'height': (number) The image height.
 *     'width': (number) The image width.
 *     'textColor': (string) The text color.
 *     'textSize': (number) The text size.
 *     'backgroundPosition: (string) The background postition x, y.
 * @param {number=} opt_padding Optional padding to apply to the cluster icon.
 * @constructor
 * @extends google.maps.OverlayView
 * @ignore
 */
function ClusterIcon(cluster, styles, pieHolePercent, opt_padding) {
    cluster.getMarkerClusterer().extend(ClusterIcon, google.maps.OverlayView);

    this.styles_ = styles;
    this.pieHolePercent_ = pieHolePercent;
    this.padding_ = opt_padding || 0;
    this.cluster_ = cluster;
    this.center_ = null;
    this.map_ = cluster.getMap();
    this.div_ = null;
    this.sums_ = null;
    this.visible_ = false;
    this.isActive_ = true;
    this.BACKGROUND_WHITE = 'rgb(251,251,251,0.5)';
    this.BACKGROUND_GREY = 'rgb(128,128,128,0.5)';
    this.BORDER_BLACK = '#000000';
    this.BORDER_WHITE = '#ffffff';
    this.DEFAULT_ICON_KEY = 'defaultIconUrl';
    this.ACTIVE_ICON_KEY = 'activeIconUrl';

    this.setMap(this.map_);
}

/**
 * Triggers the clusterclick event and zoom's if the option is set.
 */
ClusterIcon.prototype.triggerClusterClick = function () {
    var per = window.batchGeoGoogleMapsMarker.options.markersData;
    var markerClusterer = this.cluster_.getMarkerClusterer();

    // Trigger the clusterclick event.
    google.maps.event.trigger(markerClusterer, 'clusterclick', this.cluster_);

    if (markerClusterer.isZoomOnClick()) {
        // Zoom into the cluster.
        if (per.clustering_icon_col > -1 && per.clustering_icon_cb_status) {
          var doClick = false;
          if (this.cluster_.markers_.length === 1)
            doClick = true;
          var theMarker = this.cluster_.markers_[0];
          this.map_.fitBounds(this.cluster_.getBounds());
          if (doClick) {
            $(window).trigger('batchgeo.marker.click', theMarker);
            BatchGeoStore.dispatch({
              type: 'MAPSMARKERS_MANAGER_MARKER_CLICKED',
              marker: theMarker
            });
          }
        } else {
          this.map_.fitBounds(this.cluster_.getBounds());
        }
    }
};

ClusterIcon.prototype.getSum = function ( cluster ) {
	// var r = /^\$?[0-9,]+(\.[0-9][0-9])?$/;
	var valSum = 0;
	for (var t = 0; t < cluster.markers_.length; t++) {
		var numToAdd;
		if (typeof per.dataRS[cluster.markers_[t].r][per.clustering_icon_col] != "undefined")
			numToAdd = parseFloat(BatchGeo.toStr(per.dataRS[cluster.markers_[t].r][per.clustering_icon_col]).replace( /,/g, '' ).replace( /\$/g, '' ));
		else
			numToAdd = 0;
		if (Math.abs(numToAdd) > 0)
			valSum = parseFloat(valSum) + numToAdd;
	}
	valSum = (valSum == parseInt(valSum) ? parseInt(valSum) : valSum.toFixed(2));
	return valSum;
};

/**
 * Adding the cluster icon to the dom.
 * @ignore
 */
ClusterIcon.prototype.onAdd = function () {
    var per = window.batchGeoGoogleMapsMarker.options.markersData;
    this.div_ = document.createElement('DIV');
    this.div_.classList.add('cluster-icon');
    this.clickableGhostDiv_ = document.createElement('DIV');
    this.clickableGhostDiv_.classList.add('cluster-ghost');

    if (this.visible_) { 
        var pos = this.getPosFromLatLng_(this.center_);
        this.div_.style.cssText = this.createCss(pos);
        if (per.clustering_icon_col > -1 && per.clustering_icon_cb_status) {
            var moneyCol = false;
            for (var mD in per.dataRS) {
                var row = per.dataRS[mD];
                var val = row[per.clustering_icon_col];
                if (val !== undefined && val.toString().length > 0) {
                    if (!moneyCol && BatchGeo.isDollarAmount(val)) {
                        moneyCol = true;
                    }
                    if (moneyCol) {
                        break;
                    }
                }
            }

            var prepend = moneyCol ? '$' : '';
            var valSum = this.getSum(this.cluster_);
            if (valSum < 0) {
                prepend = `-${prepend}`;
            }

            if (per.clustering_icon_mode_sel === 'sum') {
                this.clusterLabel = prepend + BatchGeo.tinyNum(Math.abs(valSum));
            } else {
                this.clusterLabel = prepend + BatchGeo.tinyNum((Math.abs(valSum) / this.cluster_.markers_.length).toFixed(2));
            }
        } else {
            this.clusterLabel = this.sums_.text;
        }
        if (this.isDonutChart()) {
            this.pieHoleBackgroundDiv_ = document.createElement('DIV');
            this.pieHoleBackgroundDiv_.classList.add('cluster-background');
            this.pieHoleBackgroundDivChild_ = document.createElement('DIV');
            this.pieHoleBackgroundDivChild_.classList.add('cluster-background-child');
            const { parentStyle, childStyle } = this.createPieHoleBackgroundDivCss(pos);
            this.pieHoleBackgroundDiv_.style.cssText = parentStyle;
            this.pieHoleBackgroundDiv_.appendChild(this.pieHoleBackgroundDivChild_);
            this.pieHoleBackgroundDivChild_.style.cssText = childStyle;
        } else {
            const height = this.div_.style.height.replace(/\D/g, "");
            const el = document.createElement('p');
            let textOffset = "0px";

            if (height < 52) {
                textOffset = "-1px";
            } else if (height < 64) {
                textOffset = "-2px";
            } else {
                textOffset = "-3px";
            }

            el.style.position = "relative";
            el.style.top = textOffset;
            el.innerHTML = this.clusterLabel;
            this.div_.appendChild(el);
        }
        this.clickableGhostDiv_.style.cssText = this.createClickableGhostDivCss(pos);

    }

    var panes = this.getPanes();
    panes.markerLayer.appendChild(this.div_);
    if (this.pieHoleBackgroundDiv_) {
        panes.markerLayer.appendChild(this.pieHoleBackgroundDiv_);
    }
    panes.overlayMouseTarget.appendChild(this.clickableGhostDiv_);
    this.updateBling();

    var that = this;
    this.clickableGhostDiv_.addEventListener('click', function () {
        that.triggerClusterClick();
    });
    this.clickableGhostDiv_.addEventListener('mouseover', function() {
        that.cluster_.createOrShowHull();
    })
    this.clickableGhostDiv_.addEventListener('mouseout', function() {
        that.cluster_.hideHull();
    })
};

/**
 * Returns the position to place the div dending on the latlng.
 *
 * @param {google.maps.LatLng} latlng The position in latlng.
 * @return {google.maps.Point} The position in pixels.
 * @private
 */
ClusterIcon.prototype.getPosFromLatLng_ = function (latlng) {
    var pos = this.getProjection().fromLatLngToDivPixel(latlng);
    pos.x -= parseInt(this.width_ / 2, 10);
    pos.y -= parseInt(this.height_ / 2, 10);
    return pos;
};

/**
 * Draw the icon.
 * @ignore
 */
ClusterIcon.prototype.draw = function () {
    if (this.visible_) {
        var pos = this.getPosFromLatLng_(this.center_);
        this.div_.style.top = pos.y + 'px';
        this.div_.style.left = pos.x + 'px';
        this.clickableGhostDiv_.style.top = pos.y + 'px';
        this.clickableGhostDiv_.style.left = pos.x + 'px';
        if (this.pieHoleBackgroundDiv_) {
            this.pieHoleBackgroundDiv_.style.top = pos.y + 'px';
            this.pieHoleBackgroundDiv_.style.left = pos.x + 'px';
        }
    }
};


/**
 * Hide the icon.
 */
ClusterIcon.prototype.hide = function () {
    if (this.div_) {
        if (this.pieHoleBackgroundDiv_) {
            this.pieHoleBackgroundDiv_.style.display = 'none';
        }
        this.clickableGhostDiv_.style.display = 'none';
        this.div_.style.display = 'none';
    }
    this.visible_ = false;
};


/**
 * Position and show the icon.
 */
ClusterIcon.prototype.show = function () {
    if (this.div_) {
        var pos = this.getPosFromLatLng_(this.center_);
        this.div_.style.cssText = this.createCss(pos);
        this.div_.style.display = '';
        this.clickableGhostDiv_.style.cssText = this.createClickableGhostDivCss(pos);
        this.clickableGhostDiv_.style.display = '';
        if (this.pieHoleBackgroundDiv_ && this.pieHoleBackgroundDivChild_) {
            const {parentStyle, childStyle} = this.createPieHoleBackgroundDivCss(pos);
            this.pieHoleBackgroundDiv_.style.cssText = parentStyle;
            this.pieHoleBackgroundDiv_.style.display = 'flex';
            this.pieHoleBackgroundDivChild_.style.cssText = childStyle;
            this.pieHoleBackgroundDivChild_.style.display = '';
        }
        this.updateBling();
    }
    this.visible_ = true;
};

/**
 * Remove the icon from the map
 */
ClusterIcon.prototype.remove = function () {
    this.setMap(null);
};

/**
 * Implementation of the onRemove interface.
 * @ignore
 */
ClusterIcon.prototype.onRemove = function () {
    if (this.div_ && this.div_.parentNode) {
        this.hide();
        this.div_.parentNode.removeChild(this.div_);
        this.div_ = null;
    }
    if (this.clickableGhostDiv_ && this.clickableGhostDiv_.parentNode) {
        this.clickableGhostDiv_.parentNode.removeChild(this.clickableGhostDiv_);
        this.clickableGhostDiv_ = null;
    }
    if (this.pieHoleBackgroundDiv_ && this.pieHoleBackgroundDiv_.parentNode) {
        this.pieHoleBackgroundDiv_.parentNode.removeChild(this.pieHoleBackgroundDiv_);
        this.pieHoleBackgroundDiv_ = null;
    }
};

/**
 * Set the sums of the icon.
 *
 * @param {Object} sums The sums containing:
 *   'text': (string) The text to display in the icon.
 *   'index': (number) The style index of the icon.
 */
ClusterIcon.prototype.setSums = function (sums) {
    this.sums_ = sums;
    this.text_ = sums.text;
    this.index_ = sums.index;
    if (this.div_) {
        this.div_.innerHTML = sums.text;
    }

    this.useStyle();
};


/**
 * Sets the icon to the the styles.
 */
ClusterIcon.prototype.useStyle = function () {
    var index = Math.max(0, this.sums_.index - 1);
    index = Math.min(this.styles_.length - 1, index);
    var style = this.styles_[index];
    this.url_ = style.url;
    this.height_ = style.height;
    this.width_ = style.width;
    this.textColor_ = style.textColor;
    this.textSize_ = style.textSize;
    this.backgroundPosition_ = style.backgroundPosition;
};


/**
 * Sets the center of the icon.
 *
 * @param {google.maps.LatLng} center The latlng to set as the center.
 */
ClusterIcon.prototype.setCenter = function (center) {
    this.center_ = center;
};

/**
 * Does nothing if new state is same as old state.
 * Sets `isActive` to true.
 * Updates DOM elements if available.
 */
ClusterIcon.prototype.setActive = function () {
    if(!this.isActive_) {
        this.isActive_ = true;
        if(this.div_ && this.isDonutChart()) {
            // only update if div is available
            // otherwise it will be built later using isActive_ state
            this.div_.style.backgroundImage = `url(${this.activeIconUrl})`;
            if (this.pieHoleBackgroundDivChild_) {
                this.pieHoleBackgroundDivChild_.style.backgroundColor = this.BACKGROUND_GREY;
            }
        }
    }
};
/**
 * Does nothing if new state is same as old state.
 * Sets `isActive` to false.
 * Updates DOM elements if available.
 */
ClusterIcon.prototype.setInactive = function () {
    if(this.isActive_) {
        this.isActive_ = false;
        if (this.div_ && this.isDonutChart()) {
            // only update if div is available
            // otherwise it will be built later using isActive_ state
            this.div_.style.backgroundImage = `url(${this.defaultIconUrl})`;
            if (this.pieHoleBackgroundDivChild_) {
                this.pieHoleBackgroundDivChild_.style.backgroundColor = this.BACKGROUND_WHITE;
            }
        }
    }
};


/**
 * Checks if cluster is using a donut chart.
 * 
 * Piechart/Donut clusters are only used on maps with more than one marker group.
 * If pieHolePercent is not greater than 0, we are creating Piecharts instead of donuts.
 * @returns {Boolean}
 */
ClusterIcon.prototype.isDonutChart = function() {
    return (window.batchGeoGoogleMapsMarker.getMarkerGroupsArray().length > 1 && this.pieHolePercent_ > 0);
}

/**
 * Create donut background css text based on the position of the icon.
 *
 * @param {google.maps.Point} pos The position.
 * @return {string} The css style text.
 */
ClusterIcon.prototype.createPieHoleBackgroundDivCss = function (pos) {
    if (!this.isDonutChart()) {
        // only build if using donut cluster image
        return '';
    }

    // original size
    const originalSize = this.getOriginalSize();
    const parentStyle = `
        width: ${originalSize}px;
        height: ${originalSize}px;
        display: flex;
        justify-content: center;
        align-items: center;
        position: absolute;
        top: ${pos.y};
        left: ${pos.x};
    `;

    this.adjust = (this.height_ * 0.40);
    this.posAdjust = (this.adjust / 2);
    const newHeight = (this.height_ + this.adjust);
    
    const childStyle = `
        height: ${newHeight}px;
        width: ${newHeight}px;
        border-radius: 100%;
    `;
    
    return { parentStyle, childStyle };
};

/**
 * Create the interactible ghost div css text based on the position of the icon.
 *
 * @param {google.maps.Point} pos The position.
 * @return {string} The css style text.
 */
ClusterIcon.prototype.createClickableGhostDivCss = function (pos) {
    return `
        height: ${this.height_}px;
        width: ${this.width_}px;
        cursor: pointer;
        top: ${pos.y}px;
        left: ${pos.x}px;
        position: absolute;
        z-index:110;
        text-shadow:-1px -1px 0 rgba(255,255,255,.5),1px -1px 0 rgba(255,255,255,.5),-1px 1px 0 rgba(255,255,255,.5),1px 1px 0 rgba(255,255,255,.5);
    `;
}

/**
 * Create the css text based on the position of the icon.
 *
 * @param {google.maps.Point} pos The position.
 * @return {string} The css style text.
 */
ClusterIcon.prototype.createCss = function (pos) {
    var style = ``;
    if (window.batchGeoGoogleMapsMarker.getMarkerGroupsArray().length <= 1) {
        if (BatchGeo.isIE() && BatchGeo.isIE() <= 8) {
            style += `filter:progid:DXImageTransform.Microsoft.AlphaImageLoader(sizingMethod=scale,src="${this.url_}"); `; // 🤢🤮
        } else {
            style += `
                background-image:url(${this.url_});
                background-position: ${this.backgroundPosition_ ? this.backgroundPosition_ : '0 0'};
            `
        }
    }

    style +=`
        background-size: ${this.height_}px ${this.width_}px;
        height: ${this.height_}px;
        line-height:${this.height_}px;
        width:${this.width_}px;
        text-align:center;
        cursor: pointer;
        top: ${pos.y};
        left: ${pos.x};
        color: ${this.textColor_ ? this.textColor_ : '000'};
        position: absolute;
        font-size: ${this.textSize_ ? this.textSize_ : 12}px;
        font-family:Arial,sans-serif;
        font-weight:bold;
        display: flex; 
        justify-content: center;
        align-items: center;
        z-index:110;
        text-shadow:-1px -1px 0 rgba(255,255,255,.5),1px -1px 0 rgba(255,255,255,.5),-1px 1px 0 rgba(255,255,255,.5),1px 1px 0 rgba(255,255,255,.5);
    `;

    return style;
};

ClusterIcon.prototype.createChart = function(originalSize, pieData, scaledSize, markersData, key, borderColor, isActive = false) {
    var self = this;
    // The PieChart visualization requires an element to be passed but does
    // not actually need to be in the DOM. We create an element on the fly
    // here but don't ever append it because we never want to actually embed
    // the pie chart. We later will export this chart as an image.
    var chart = new google.visualization.PieChart(document.createElement('div'));

    // Once the chart is ready we can get the chart as an image and then
    // render it into the `div_` element.
    google.visualization.events.addListener(chart, 'ready', function () {
        // save to self
        self[key] = chart.getImageURI();

        if(isActive) {
            // use current chart
            const marginTop = `${self.calculateMarginTop(originalSize)}px`;
            const marginLeft = `${self.calculateMarginLeft(originalSize)}px`;
            $(self.div_).css({
                width: originalSize,
                height: originalSize,
                backgroundImage: `url(${self[key]})`,
                backgroundSize: originalSize + "px " + originalSize + "px",
                marginTop,
                marginLeft
            });
            if(self.isDonutChart()) { 
                // donut chart updates
                $(self.pieHoleBackgroundDiv_).css({ // position the pieHole background div
                    width: originalSize,
                    height: originalSize,
                    marginTop,
                    marginLeft
                });
                $(self.pieHoleBackgroundDivChild_).css({ // set pieHole background div child color
                    backgroundColor: self.isActive_ ? self.BACKGROUND_GREY : self.BACKGROUND_WHITE
                });
            }
            self.div_.innerHTML = self.clusterLabel; // add the cluster label to dom
        }
    });

    // Draws the chart. It's important to note this is async so you can't
	// assume after calling this it's visible or available.
    chart.draw(google.visualization.arrayToDataTable(pieData), {
        // Hide the title, background, and legend
        title: null,
        backgroundColor: { fill:'transparent' },
        legend: 'none',
        // Use the scaledSize (bigger size) than we need so we then can
        // later scale it down to make the image crisp
        width: scaledSize,
        height: scaledSize,
        colors: markersData.colors.map(function (color) {
            return hexArray[colorPallet.indexOf(color)]
        }),
        pieSliceBorderColor: borderColor,
        pieSliceText: 'none',
        pieHole: self.pieHolePercent_,
        // Since we're gonna export as a PNG we can shave off a bit of load
        // time by disabling this.
        enableInteractivity: false
    });
}

/**
 * 	This is the the pie chart size. The 2.5 is a magic number in that it
	is set to 2.5 just to make it look good based on the width given.
 * @returns {number}
 */
ClusterIcon.prototype.getOriginalSize = function() {
    var originalSize = parseInt(this.width_ * 2.5);
    if (originalSize % 2 !== 0) originalSize++; // always even
    return originalSize;
}
ClusterIcon.prototype.calculateMarginTop = function(originalSize) {
    return -((originalSize - this.height_) / 2);
}
ClusterIcon.prototype.calculateMarginLeft = function(originalSize) {
    return -((originalSize - this.width_) / 2);
}

ClusterIcon.prototype.updateBling = function () {
    var markersData = window.batchGeoGoogleMapsMarker.options.markersData;

    window.hexOfColors = function () { // output current colors in hex
		var returnArr = [];
		for (var c=0; c<markersData.colors.length; c++) {
			returnArr[c] = '#' + hexArray[colorPallet.indexOf(markersData.colors[c])];
		}
		return returnArr;
	};

    if (this.width_ && window.batchGeoGoogleMapsMarker.getMarkerGroupsArray().length > 1) {
        var clust = this.cluster_;

        var colorCount = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
        var t, c;
        for (t = 0; t < clust.markers_.length; t++) {
            for (c = 0; c < markersData.colors.length; c++) {
                if (markersData.mapRS[clust.markers_[t].r].clr == markersData.colors[c]) {
                    colorCount[c]++;
                    break;
                }
            }
        }
		// Google Charts needs very specific format which is this:
		// [
		//   ['the', 'headers'],
		//   ['a label', the_value]
		// ]
		// Example for our use case:
		// [
		//   ['', ''] // This is the header (we don't need this tho)
		//   ['', 10] // String is the label (we don't need it) & actual value
		// ]
		var pieData = [['','']].concat(markersData.colors.map(function (color, index) {
			// Take the colorCount value / total markers in the cluster * 100 to
			// get the percentage value
			return ['', parseInt((colorCount[index] / clust.markers_.length) * 100)]
		}));

		// This is the the pie chart size. The 2.5 is a magic number in that it
		// is set to 2.5 just to make it look good based on the width given.
        var originalSize = this.getOriginalSize();
		// The scaledSize var gets the device pixel ratio (i.e. 2x or 3x
		// depending on your device.) and multiplies the original size by that.
		// This will make it so we request 2x/3x bigger than we need and then we
		// scale it down so the image (a PNG) looks crisp.
		var scaledSize = parseInt(originalSize * BatchGeo.getDevicePixelRatio());

        // We create two versions of every piechart to enable selection of clusters.
        this.createChart(originalSize, pieData, scaledSize, markersData, this.DEFAULT_ICON_KEY, this.BORDER_WHITE, !clust.isIconSelected);
        this.createChart(originalSize, pieData, scaledSize, markersData, this.ACTIVE_ICON_KEY, this.BORDER_BLACK, clust.isIconSelected);
    }
};

window.Clarker = Clarker;
